import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../utils/axios';

const Profile = () => {
  const { t } = useTranslation();
  const { updateProfile, changePassword } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get('/api/auth/me');
      console.log('User data received:', data); // Debug log
      setProfileData({
        name: data.name || '',
        email: data.email || '',
        location: data.location || '',
        bio: data.bio || '',
        phoneNumber: data.phoneNumber || '',
        newsletterSubscribed: data.newsletterSubscribed || false,
      });
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(t('errorFetchingProfile'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateProfile(profileData);
      await fetchUserData(); // Refresh data after update
      setSuccess(t('profileUpdateSuccess'));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('profileUpdateError'));
      setSuccess(null);
    }
  };

  const handleNewsletterChange = async (newValue) => {
    try {
      await axiosInstance.put('/api/auth/profile', {
        newsletterSubscribed: newValue
      });
      await fetchUserData(); // Refresh data after update
      setSuccess(t(newValue ? 'newsletterSubscribeSuccess' : 'newsletterUnsubscribeSuccess'));
      setError(null);
    } catch (err) {
      setError(t('newsletterUpdateError'));
      setSuccess(null);
    }
  };

  if (isLoading || !profileData) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase();
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('passwordMismatch'));
      return;
    }
    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setSuccess(t('updatePasswordSuccess'));
      setError(null);
      setOpenPasswordDialog(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      setError(err.response?.data?.message || t('passwordChangeError'));
      setSuccess(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('profileInformation')}
            </Typography>
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Stack spacing={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    sx={{ 
                      width: 100, 
                      height: 100, 
                      mr: 2,
                      bgcolor: 'primary.main',
                      fontSize: '2rem'
                    }}
                  >
                    {getInitials(profileData.name)}
                  </Avatar>
                </Box>
                <TextField
                  name="name"
                  label={t('name')}
                  value={profileData.name}
                  onChange={handleProfileChange}
                  fullWidth
                  required
                />
                <TextField
                  name="email"
                  label={t('email')}
                  value={profileData.email}
                  onChange={handleProfileChange}
                  fullWidth
                  required
                  type="email"
                />
                <TextField
                  name="phoneNumber"
                  label={t('phoneNumber')}
                  value={profileData.phoneNumber}
                  onChange={handleProfileChange}
                  fullWidth
                  helperText={t('phoneNumberFormat')}
                  placeholder="0612345678"
                />
                <TextField
                  name="location"
                  label={t('location')}
                  value={profileData.location}
                  onChange={handleProfileChange}
                  fullWidth
                />
                <TextField
                  name="bio"
                  label={t('bio')}
                  value={profileData.bio}
                  onChange={handleProfileChange}
                  fullWidth
                  multiline
                  rows={4}
                />
                <Button type="submit" variant="contained">
                  {t('updateProfile')}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('security')}
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setOpenPasswordDialog(true)}
              >
                {t('changePassword')}
              </Button>
            </Paper>

            <Paper elevation={0} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('preferences')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={profileData.newsletterSubscribed}
                    onChange={(e) => handleNewsletterChange(e.target.checked)}
                  />
                }
                label={t(profileData.newsletterSubscribed ? 'newsletterSubscribed' : 'newsletterNotSubscribed')}
              />
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)}>
        <DialogTitle>{t('changePassword')}</DialogTitle>
        <form onSubmit={handlePasswordSubmit}>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Stack spacing={3}>
                <TextField
                  name="currentPassword"
                  label={t('currentPassword')}
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  fullWidth
                  required
                />
                <TextField
                  name="newPassword"
                  label={t('newPassword')}
                  type="password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  fullWidth
                  required
                />
                <TextField
                  name="confirmPassword"
                  label={t('newPasswordConfirmation')}
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  fullWidth
                  required
                />
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button type="button" onClick={() => setOpenPasswordDialog(false)}>
              {t('cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {t('updatePassword')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default Profile; 